import { watch, getCurrentScope, onScopeDispose } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQueryStore } from '~/store/query';
import isEmpty from 'lodash/isEmpty';

export function useQueryManager() {
  const store = useQueryStore();
  const route = useRoute();
  const router = useRouter();

  const initialPath = route.path;

  store.bulkSetQuery(route.query);

  const stopQueryWatcher = watch(
    () => store.query,
    newQuery => {
      // Once the user has left this page, the new page owns the URL, so clearing on the way out must not rewrite it.
      if (!route.path.startsWith(initialPath)) return;

      const cleaned = Object.fromEntries(Object.entries(newQuery).filter(([_, v]) => !isEmpty(v)));

      router.replace({
        path: route.path,
        query: cleaned,
      });
    },
    { deep: true },
  );

  const stopPathWatcher = watch(
    () => route.fullPath,
    newPath => {
      if (!newPath.startsWith(initialPath)) {
        store.clearQuery();
      }
    },
  );

  const scope = getCurrentScope();
  if (scope) {
    onScopeDispose(() => {
      stopQueryWatcher();
      stopPathWatcher();
    });
  }

  function setQueryParam(key: string, value: string | number | null | undefined) {
    if (value === undefined || value === null || value === '') {
      store.removeQueryKey(key);
    } else {
      store.setQuery(key, value.toString());
    }
  }

  function setQueryParams(params: Record<string, any>) {
    for (const key in params) {
      setQueryParam(key, params[key]);
    }
  }

  function getQueryParam(key: string) {
    return store.query[key];
  }

  return {
    getQueryParam,
    query: store.query,
    setQueryParam,
    setQueryParams,
  };
}
